import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  RefObject,
} from 'react';
import classNames from 'classnames';

import usePrevious from 'js/utils/hooks/usePrevious';
import { Icon } from 'js/design_system/Icon';

import 'app/assets/stylesheets/design_system/modal.scss';

/**
 * Context used to keep track of the closest parent modal, so things like
 * popovers can render atop the modal.
 */
export const ModalElementContext =
  React.createContext<RefObject<HTMLElement> | null>(null);

export type Props = React.DialogHTMLAttributes<HTMLDialogElement> &
  React.PropsWithChildren<{
    /**
     * For custom styling of the modal container.
     * This prop can be passed manually, or using astroturf via `styled(Modal)`.
     */
    className?: string;
    /**
     * Whether the modal is open or closed.
     * This state should be maintained by the parent component with useState or similar.
     */
    open: boolean;
    /**
     * Called when clicking the close button or the backdrop.
     * This must be passed by the parent component, and should usually set the `open` state to false.
     */
    onClose: () => void;
    /**
     * Heading text for the modal.
     */
    title?: string;
    /**
     * Whether to show the header, which contains the title and close button.
     * Note that modals without headers will need to be closed manually or by clicking the backdrop.
     */
    showHeader?: boolean;

    containerStyle?: React.CSSProperties;
  }>;

/**
 * A modal dialog component with Academia styling. This component can be use to create a dialog
 * that appears over other content, with a darkened backdrop and scrolling prevented on the
 * rest of the page.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Button } from 'js/design_system/Button';
 * import { TextButton } from 'js/design_system/TextButton';
 * import { Modal, ModalContent, ModalButtonGroup } from 'js/design_system/Modal';
 *
 * const [open, setOpen] = useState(false);
 *
 * <Button onClick={() => setOpen(true)}>Open Modal</Button>
 * <Modal
 *   title="Modal title"
 *   open={open}
 *   onClose={() => setOpen(false)}
 * >
 *   <ModalContent>
 *    <p>Modal content goes here!</p>
 *    <ModalButtonGroup>
 *     <TextButton onClick={() => setOpen(false)}>Close</Button>
 *     <Button onClick={() => setOpen(false)}>Confirm</Button>
 *   </ModalButtonGroup>
 *  </ModalContent>
 * </Menu>
 * ```
 */
export const Modal = React.forwardRef<HTMLDialogElement, Props>(
  (
    {
      children,
      className,
      onClose,
      open = false,
      title,
      showHeader = true,
      containerStyle,
      ...htmlDialogProps
    },
    dialogRef,
  ) => {
    const innerRef = useRef<HTMLDialogElement>(null);

    useImperativeHandle(
      dialogRef,
      () => innerRef.current as HTMLDialogElement,
      [innerRef],
    );

    const previousOpen = usePrevious(open);

    useEffect(() => {
      if (open && !previousOpen) {
        innerRef.current?.showModal();
      } else if (!open && previousOpen) {
        innerRef.current?.close();
      }
    }, [open, previousOpen]);

    return (
      <dialog
        {...htmlDialogProps}
        className={classNames('ds2-5-modal', className)}
        ref={innerRef}
        role="modal"
        onCancel={(event: React.SyntheticEvent<HTMLDialogElement>) => {
          // Handle the native dialog's cancel event (usually fired via 'Esc' key press)
          onClose();

          // Prevent the native dialog from closing the modal
          event.preventDefault();
        }}
        onMouseDown={(event) => {
          // Handle backdrop clicks
          const isBackdrop = event.target === event.currentTarget;

          if (isBackdrop) {
            onClose();

            // Prevent the native dialog from closing the modal
            event.preventDefault();
          }
        }}
        // Prevent propagation outside of the modal
        onClick={(event) => event.stopPropagation()}
      >
        <ModalElementContext.Provider value={innerRef}>
          <div className="ds2-5-modal__container" style={containerStyle}>
            {showHeader && (
              <div
                className={classNames('ds2-5-modal__header', {
                  'ds2-5-modal__header--empty': !title,
                })}
              >
                {title}
                <button
                  className="ds2-5-modal__close-button"
                  aria-label="Close"
                  onClick={onClose}
                  type="button"
                >
                  <Icon name="close" size={24} />
                </button>
              </div>
            )}
            {children}
          </div>
        </ModalElementContext.Provider>
      </dialog>
    );
  },
);

/**
 * The inner content of a modal dialog, not including the modal's header and padding. Using this
 * component together with Modal and ModalButtonGroup will give you a complete, standard modal
 * dialog.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Button } from 'js/design_system/Button';
 * import { TextButton } from 'js/design_system/TextButton';
 * import { Modal, ModalContent, ModalButtonGroup } from 'js/design_system/Modal';
 *
 * const [open, setOpen] = useState(false);
 *
 * <Button onClick={() => setOpen(true)}>Open Modal</Button>
 * <Modal
 *   title="Modal title"
 *   open={open}
 *   onClose={() => setOpen(false)}
 * >
 *   <ModalContent>
 *    <p>Modal content goes here!</p>
 *    <ModalButtonGroup>
 *     <TextButton onClick={() => setOpen(false)}>Close</Button>
 *     <Button onClick={() => setOpen(false)}>Confirm</Button>
 *   </ModalButtonGroup>
 *  </ModalContent>
 * </Menu>
 * ```
 */
export const ModalContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...htmlDivProps }, ref) => {
  return (
    <div
      {...htmlDivProps}
      ref={ref}
      className={classNames('ds2-5-modal__content', className)}
    >
      {children}
    </div>
  );
});

/**
 * Standard container for the CTA button group at the bottom of a modal dialog. Using this
 * component together with Modal and ModalContent will give you a complete, standard modal
 * dialog.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Button } from 'js/design_system/Button';
 * import { TextButton } from 'js/design_system/TextButton';
 * import { Modal, ModalContent, ModalButtonGroup } from 'js/design_system/Modal';
 *
 * const [open, setOpen] = useState(false);
 *
 * <Button onClick={() => setOpen(true)}>Open Modal</Button>
 * <Modal
 *   title="Modal title"
 *   open={open}
 *   onClose={() => setOpen(false)}
 * >
 *   <ModalContent>
 *    <p>Modal content goes here!</p>
 *    <ModalButtonGroup>
 *     <TextButton onClick={() => setOpen(false)}>Close</Button>
 *     <Button onClick={() => setOpen(false)}>Confirm</Button>
 *   </ModalButtonGroup>
 *  </ModalContent>
 * </Menu>
 * ```
 */
export const ModalButtonGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { size?: 'default' | 'small' }
>(({ children, className, size = 'default', ...htmlDivProps }, ref) => {
  return (
    <div
      {...htmlDivProps}
      ref={ref}
      className={classNames(
        'ds2-5-modal__button-group',
        `ds2-5-modal__button-group--${size}`,
        className,
      )}
    >
      {children}
    </div>
  );
});
